import { thumbnailConstants } from "@/constants/thumbnailConstants";
import { DataObject, ScanDataObject } from "@/typed/CaseStateData";
import * as THREE from "three";
import { BufferGeometryUtils, PLYLoader, STLLoader } from "three/examples/jsm/Addons.js";

onmessage = (e: MessageEvent<Message>) => {
  if (e?.data !== undefined) {
    handleMessage(e?.data);
  }
};

const handleMessage = async (message: Message) => {
  try {
    if (message?.kind === undefined) {
      postMessage(undefined);
      return;
    }
    switch (message.kind) {
      case "geometryFromData":
        postGeometry(generateGeometry(message.data));
        return;
      case "thumbnailFromGeometry":
        postArrayBuffer(await generateThumbnail(bufferGeometryFromGeometry(message.data)));
        return;
      case "thumbnailFromData": {
        const bufferGeo = generateGeometry(message.data);
        if (bufferGeo === undefined) {
          postMessage(undefined);
          return;
        }

        postArrayBuffer(await generateThumbnail(bufferGeo));
        return;
      }
      default: {
        const _exhaustiveCheck: never = message; // ensures all possibilities have been handled
        postMessage(undefined);
        return;
      }
    }
  } catch (err) { //eslint-disable-line
    postMessage(undefined);
  }
};

const loaders: Record<string, any> = {
  stl: STLLoader,
  ply: PLYLoader
};

const loadModel = (data: ScanDataObject | DataObject): THREE.BufferGeometry | undefined => {
  const Loader = loaders?.[data.type];
  if (Loader === undefined) {
    return;
  }

  try {
    const loader = new Loader();
    return loader.parse(data.data);
  } catch (e) { //eslint-disable-line
    return;
  }
};

const generateGeometry = (data: (ScanDataObject | DataObject)[]) => {
  let geometries: THREE.BufferGeometry[] = [];
  for (let i = 0; i < data.length && geometries.length < 2; i++) {
    // Only do first two scans if more are present as they are sorted to be
    // upper and lower
    const geometry = loadModel(data[i]);
    if (geometry !== undefined) {
      geometries.push(geometry);
    }
  }

  const numGeometries = geometries.length;
  let hasColor = 0;
  let hasIndex = 0;
  for (const geometry of geometries) {
    if (geometry.hasAttribute("color")) {
      hasColor++;
    }
    if (geometry.getIndex() !== null) {
      hasIndex++;
    }
  }

  if (hasColor != numGeometries) {
    for (const geometry of geometries) {
      if (geometry.hasAttribute("color")) {
        geometry.deleteAttribute("color");
      }
    }
  }
  if (hasIndex !== numGeometries && hasIndex !== 0) {
    geometries = geometries.filter((geometry) => geometry.getIndex() !== null);
  }
  geometries = geometries.filter((geometry) => geometry.getAttribute("position").count !== 0);

  if (geometries.length === 0) {
    return undefined;
  }

  const geometry = geometries.length > 1 ? BufferGeometryUtils.mergeGeometries(geometries) : geometries[0];
  geometry.computeVertexNormals(); // Must be computed for directional lights to work

  return geometry;
};

const generateThumbnail = (geometry: THREE.BufferGeometry) =>
  new Promise<ArrayBuffer | undefined>((resolve) => {
    const hasVertexColors = geometry.getAttribute("color") !== undefined;

    // Setup Three.js scene
    const scene = new THREE.Scene();
    const camera = new THREE.OrthographicCamera();
    camera.position.set(0, 0, 5);
    const COLOUR_WHITE = "#ffffff";

    const canvas = new OffscreenCanvas(thumbnailConstants.THUMBNAIL_SIZE, thumbnailConstants.THUMBNAIL_SIZE);
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, canvas: canvas });

    // Maintains colour if it exists.
    const material = new THREE.MeshStandardMaterial({
      metalness: 0.5,
      roughness: 0.5,
      color: hasVertexColors ? COLOUR_WHITE : thumbnailConstants.THUMBNAIL_MODEL_COLOUR,
      vertexColors: hasVertexColors,
      side: THREE.DoubleSide
    });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.castShadow = true; // Cast shadows for the mesh
    mesh.receiveShadow = true; // Make sure the mesh can receive shadows

    scene.add(mesh);

    const box = new THREE.Box3().setFromObject(mesh);

    const size = box.getSize(new THREE.Vector3());
    const maxDimension = Math.max(size.x, size.y);
    const scaleFactor = 2 / maxDimension; // using factor of 2 so the model's max dim is -1 to 1
    mesh.scale.set(scaleFactor, scaleFactor, scaleFactor);
    const scaledBox = new THREE.Box3().setFromObject(mesh);
    const center = scaledBox.getCenter(new THREE.Vector3());
    mesh.position.sub(center); // Shift mesh so that its center is at (0, 0, 0)

    // Make the camera look at the mesh's position
    camera.lookAt(new THREE.Vector3(0, 0, 0));

    const dirLight1 = new THREE.DirectionalLight(COLOUR_WHITE, 5);
    const dirLight2 = new THREE.DirectionalLight(COLOUR_WHITE, 1);
    dirLight1.position.set(3, 5, 5);
    dirLight2.position.set(-8, -10, -8);
    scene.add(new THREE.AmbientLight(new THREE.Color(thumbnailConstants.THUMBNAIL_MODEL_LIGHT), Math.PI));
    scene.add(dirLight1);
    scene.add(dirLight2);
    renderer.render(scene, camera);

    (async () => {
      const blob = await canvas?.convertToBlob({ type: "image/png" });
      const arrayBuffer = await blob?.arrayBuffer();
      resolve(arrayBuffer);
    })();
  });

const postArrayBuffer = (buffer?: ArrayBuffer) => {//eslint-disable-line
  postMessage(buffer, { transfer: [...(buffer !== undefined ? [buffer] : [])] });
};

const postGeometry = (geometry: THREE.BufferGeometry | undefined) => {//eslint-disable-line
  if (geometry === undefined) {
    postMessage(undefined);
    return;
  }

  const data = geometryToPostable(geometry);
  postMessage(data, {
    transfer: [
      data.position.array.buffer,
      ...(data.color !== undefined ? [data.color.array.buffer] : []),
      ...(data.index !== undefined ? [data.index.array.buffer] : [])
    ]
  });
};

export const bufferGeometryFromGeometry = (data: Geometry): THREE.BufferGeometry => {
  const geometry = new THREE.BufferGeometry();
  if (data.color !== undefined) {
    geometry.setAttribute("color", new THREE.BufferAttribute(data.color.array, data.color.itemSize));
  }
  if (data.index !== undefined) {
    geometry.setIndex(new THREE.BufferAttribute(data.index.array, data.index.itemSize));
  }
  geometry.setAttribute("position", new THREE.BufferAttribute(data.position.array, data.position.itemSize));
  geometry.setAttribute("normal", new THREE.BufferAttribute(data.normal.array, data.normal.itemSize));

  return geometry;
};

export const geometryToPostable = (geometry: THREE.BufferGeometry): Geometry => {
  const index = geometry.getIndex();
  return {
    color: geometry.hasAttribute("color")
      ? {
          array: geometry.getAttribute("color").array,
          itemSize: geometry.getAttribute("color").itemSize
        }
      : undefined,
    position: {
      array: geometry.getAttribute("position").array,
      itemSize: geometry.getAttribute("position").itemSize
    },
    normal: {
      array: geometry.getAttribute("normal").array,
      itemSize: geometry.getAttribute("normal").itemSize
    },
    index:
      index !== null
        ? {
            array: index.array,
            itemSize: index.itemSize
          }
        : undefined
  };
};

export interface Geometry {
  color?: {
    array: THREE.TypedArray;
    itemSize: number;
  };
  position: {
    array: THREE.TypedArray;
    itemSize: number;
  };
  normal: {
    array: THREE.TypedArray;
    itemSize: number;
  };
  index?: {
    array: THREE.TypedArray;
    itemSize: number;
  };
}

export interface GenerateGeometryFromDataMessage {
  kind: "geometryFromData";
  data: (ScanDataObject | DataObject)[];
}

export interface GenerateThumbnailFromGeometry {
  kind: "thumbnailFromGeometry";
  data: Geometry;
}

export interface GenerateThumbnailFromData {
  kind: "thumbnailFromData";
  data: (ScanDataObject | DataObject)[];
}

export type Message = GenerateGeometryFromDataMessage | GenerateThumbnailFromData | GenerateThumbnailFromGeometry;
